import Axios, { AxiosError } from "axios";
import { HttpHeaderKey } from "common/enums";
import { ApiResult } from "common/models";
import { msalInstance } from "common/config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export class ExternalApiClient {
  /**
   * HTTP GET method for external APIs that don't use the base URL
   */
  public static async get<T>(
    url: string,
    headers: Record<string, string> = {}
  ): Promise<ApiResult<T>> {
    // Add authentication if needed
    const request = {
      scopes: [process.env.REACT_APP_API_SCOPE || ""]
    };

    let tokenResult;
    try {
      tokenResult = await msalInstance.acquireTokenSilent(request);
      headers.Authorization = `Bearer ${tokenResult.accessToken}`;
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        // Prompt the user for consent interactively
        try {
          tokenResult = await msalInstance.acquireTokenPopup(request);
          headers.Authorization = `Bearer ${tokenResult.accessToken}`;
        } catch (popupError) {
          console.warn(
            "Failed to acquire token, proceeding without authentication:",
            popupError
          );
        }
      } else {
        console.warn(
          "Failed to acquire token, proceeding without authentication:",
          error
        );
      }
    }

    const axiosOpts = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...headers
      },
      timeout: 30000 // 30 second timeout for external APIs
    };

    try {
      const axiosResult = await Axios.get(url, axiosOpts);
      return new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      const axiosError = error as AxiosError;
      return new ApiResult<any>(
        axiosError?.response?.data,
        axiosError.response?.status,
        axiosError.message
      );
    }
  }

  /**
   * HTTP POST method for external APIs that don't use the base URL
   */
  public static async post<T>(
    url: string,
    data: any,
    headers: Record<string, string> = {}
  ): Promise<ApiResult<T>> {
    // Add authentication if needed
    const request = {
      scopes: [process.env.REACT_APP_API_SCOPE || ""]
    };

    let tokenResult;
    try {
      tokenResult = await msalInstance.acquireTokenSilent(request);
      headers.Authorization = `Bearer ${tokenResult.accessToken}`;
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        // Prompt the user for consent interactively
        try {
          tokenResult = await msalInstance.acquireTokenPopup(request);
          headers.Authorization = `Bearer ${tokenResult.accessToken}`;
        } catch (popupError) {
          console.warn(
            "Failed to acquire token, proceeding without authentication:",
            popupError
          );
        }
      } else {
        console.warn(
          "Failed to acquire token, proceeding without authentication:",
          error
        );
      }
    }

    const axiosOpts = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...headers
      },
      timeout: 30000 // 30 second timeout for external APIs
    };

    try {
      const axiosResult = await Axios.post(url, data, axiosOpts);
      return new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      const axiosError = error as AxiosError;
      return new ApiResult<any>(
        axiosError?.response?.data,
        axiosError.response?.status,
        axiosError.message
      );
    }
  }

  /**
   * HTTP PUT method for external APIs that don't use the base URL
   */
  public static async put<T>(
    url: string,
    data: any,
    headers: Record<string, string> = {}
  ): Promise<ApiResult<T>> {
    // Add authentication if needed
    const request = {
      scopes: [process.env.REACT_APP_API_SCOPE || ""]
    };

    let tokenResult;
    try {
      tokenResult = await msalInstance.acquireTokenSilent(request);
      headers.Authorization = `Bearer ${tokenResult.accessToken}`;
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        // Prompt the user for consent interactively
        try {
          tokenResult = await msalInstance.acquireTokenPopup(request);
          headers.Authorization = `Bearer ${tokenResult.accessToken}`;
        } catch (popupError) {
          console.warn(
            "Failed to acquire token, proceeding without authentication:",
            popupError
          );
        }
      } else {
        console.warn(
          "Failed to acquire token, proceeding without authentication:",
          error
        );
      }
    }

    const axiosOpts = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...headers
      },
      timeout: 30000 // 30 second timeout for external APIs
    };

    try {
      const axiosResult = await Axios.put(url, data, axiosOpts);
      return new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      const axiosError = error as AxiosError;
      return new ApiResult<any>(
        axiosError?.response?.data,
        axiosError.response?.status,
        axiosError.message
      );
    }
  }

  /**
   * HTTP DELETE method for external APIs that don't use the base URL
   */
  public static async delete<T>(
    url: string,
    headers: Record<string, string> = {}
  ): Promise<ApiResult<T>> {
    // Add authentication if needed
    const request = {
      scopes: [process.env.REACT_APP_API_SCOPE || ""]
    };

    let tokenResult;
    try {
      tokenResult = await msalInstance.acquireTokenSilent(request);
      headers.Authorization = `Bearer ${tokenResult.accessToken}`;
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        // Prompt the user for consent interactively
        try {
          tokenResult = await msalInstance.acquireTokenPopup(request);
          headers.Authorization = `Bearer ${tokenResult.accessToken}`;
        } catch (popupError) {
          console.warn(
            "Failed to acquire token, proceeding without authentication:",
            popupError
          );
        }
      } else {
        console.warn(
          "Failed to acquire token, proceeding without authentication:",
          error
        );
      }
    }

    const axiosOpts = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...headers
      },
      timeout: 30000 // 30 second timeout for external APIs
    };

    try {
      const axiosResult = await Axios.delete(url, axiosOpts);
      return new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      const axiosError = error as AxiosError;
      return new ApiResult<any>(
        axiosError?.response?.data,
        axiosError.response?.status,
        axiosError.message
      );
    }
  }
}
